/**
 * ConfigManager - Unified Configuration Lifecycle Management
 *
 * Manages all configuration concerns for the grid:
 * - Source collection (gridConfig, columns, attributes, Light DOM)
 * - Two-layer config (frozen original + mutable effective)
 * - State persistence (collect/apply/reset via diff)
 * - Change notification for re-rendering
 *
 * This is an internal module - grid.ts delegates to ConfigManager
 * but the public API remains unchanged.
 */

import type { BaseGridPlugin } from '../plugin';
import type {
  ColumnConfig,
  ColumnConfigMap,
  ColumnInferenceMode,
  ColumnInternal,
  ColumnSortState,
  ColumnState,
  FitMode,
  GridColumnState,
  GridConfig,
  GridHost,
  TypeDefault,
} from '../types';
import {
  applyInitialOrder,
  mergeColumns,
  parseLightDomColumns,
  parseLightDomTypeDefaults,
  updateTemplate,
} from './columns';
import { renderHeader } from './header';
import { inferColumns, overlayInferred } from './inference';
import { RenderPhase } from './render-scheduler';
import { compileTemplate } from './sanitize';

/** Debounce timeout for state change events */
const STATE_CHANGE_DEBOUNCE_MS = 100;

/** Custom property the row/cell CSS binds its `min-height` to. */
const ROW_HEIGHT_VAR = '--tbw-row-height';

/**
 * Column properties that are never inherited from `gridConfig.typeDefaults` —
 * they identify or position a single column rather than describe its type.
 * Mirrors the `NonInheritableTypeDefaultKey` union in `core/types.ts`.
 */
const NON_INHERITABLE_TYPE_DEFAULT_KEYS = new Set(['field', 'header', 'order', 'group', 'hidden', 'utility']);

/**
 * Copy every own key of the matching type default onto each column that does
 * not already define it.
 *
 * Property-agnostic by design: plugin-augmented column properties (`editor`,
 * `filterable`, …) work without core knowing about them. Runs before the
 * per-column defaults so a type default can override `sortable`/`resizable`.
 */
function applyTypeDefaults<T>(columns: ColumnInternal<T>[], typeDefaults: Record<string, TypeDefault<T>>): void {
  for (const column of columns) {
    const typeDefault = column.type ? typeDefaults[column.type] : undefined;
    if (!typeDefault) continue;

    // Reflect, not an indexed cast: the copy is key-driven, so neither side can
    // be walked through its declared shape.
    for (const key of Object.keys(typeDefault)) {
      if (NON_INHERITABLE_TYPE_DEFAULT_KEYS.has(key)) continue;
      const value: unknown = Reflect.get(typeDefault, key);
      if (value === undefined) continue;
      if (Reflect.get(column, key) != null) continue;
      // `renderer` and `viewRenderer` are aliases — either one on the column
      // suppresses both type-level forms.
      if ((key === 'renderer' || key === 'viewRenderer') && (column.renderer != null || column.viewRenderer != null))
        continue;
      Reflect.set(column, key, value);
    }
  }
}

/**
 * ConfigManager handles all configuration lifecycle for the grid.
 *
 * Manages:
 * - Source collection (gridConfig, columns, attributes, Light DOM)
 * - Effective config (merged from all sources)
 * - State persistence (collectState, applyState, resetState)
 * - Column visibility and ordering
 */
export class ConfigManager<T = unknown> {
  // #region Sources (raw input from user)
  #gridConfig?: GridConfig<T>;
  #columns?: ColumnConfig<T>[] | ColumnConfigMap<T>;
  #fitMode?: FitMode;
  #columnInference?: ColumnInferenceMode;

  // Light DOM cache
  #lightDomColumnsCache?: ColumnInternal<T>[];
  #lightDomTypeDefaultsCache?: Record<string, TypeDefault<T>>;
  #originalColumnNodes?: HTMLElement[];
  // #endregion

  // #region Two-Layer Config Architecture
  /**
   * Original config (frozen) - Built from sources, never mutated.
   * This is the "canonical" config that sources compile into.
   * Used as the reset point for effectiveConfig.
   */
  #originalConfig: GridConfig<T> = {};

  /**
   * Effective config (mutable) - Cloned from original, runtime mutations here.
   * This is what rendering reads from.
   * Runtime changes: hidden, width, sort order, column order.
   */
  #effectiveConfig: GridConfig<T> = {};
  // #endregion

  // #region State Tracking
  #sourcesChanged = true;
  #lightDomObserver?: MutationObserver;
  #stateChangeTimeoutId?: ReturnType<typeof setTimeout>;
  #lightDomDebounceTimer?: ReturnType<typeof setTimeout>;
  #initialColumnState?: GridColumnState;
  #grid: GridHost<T>;
  /** Whether the inline `--tbw-row-height` on the host was written by us and may be cleared. */
  #ownsRowHeightVar = false;

  constructor(grid: GridHost<T>) {
    this.#grid = grid;
  }
  // #endregion

  // #region Getters
  /** Get the frozen original config (compiled from sources, immutable) */
  get original(): GridConfig<T> {
    return this.#originalConfig;
  }

  /** Get the mutable effective config (current runtime state) */
  get effective(): GridConfig<T> {
    return this.#effectiveConfig;
  }

  /** Get columns from effective config */
  get columns(): ColumnInternal<T>[] {
    return (this.#effectiveConfig.columns ?? []) as ColumnInternal<T>[];
  }

  /** Set columns on effective config */
  set columns(value: ColumnInternal<T>[]) {
    this.#effectiveConfig.columns = value as ColumnConfig<T>[];
  }

  /** Get light DOM columns cache */
  get lightDomColumnsCache(): ColumnInternal<T>[] | undefined {
    return this.#lightDomColumnsCache;
  }

  /** Set light DOM columns cache */
  set lightDomColumnsCache(value: ColumnInternal<T>[] | undefined) {
    this.#lightDomColumnsCache = value;
  }

  /** Get original column nodes */
  get originalColumnNodes(): HTMLElement[] | undefined {
    return this.#originalColumnNodes;
  }

  /** Set original column nodes */
  set originalColumnNodes(value: HTMLElement[] | undefined) {
    this.#originalColumnNodes = value;
  }

  /** Get initial column state */
  get initialColumnState(): GridColumnState | undefined {
    return this.#initialColumnState;
  }

  /** Set initial column state */
  set initialColumnState(value: GridColumnState | undefined) {
    this.#initialColumnState = value;
  }
  // #endregion

  // #region Source Management
  /**
   * Mark that sources have changed and need re-merging.
   * Call this when external state (shell maps, etc.) that feeds into
   * collectAllSources() has been updated.
   */
  markSourcesChanged(): void {
    this.#sourcesChanged = true;
  }
  // #endregion

  // #region Source Setters
  /** Set gridConfig source */
  setGridConfig(config: GridConfig<T> | undefined): void {
    // Identity short-circuit: if the same reference is set again (common with
    // memoized framework configs whose ref callback re-runs on every render),
    // do nothing. Flipping #sourcesChanged here would cause the next merge()
    // to rebuild #effectiveConfig.columns from the original config, wiping
    // runtime mutations like col.hidden written by setColumnVisible() /
    // applyColumnState().
    if (this.#gridConfig === config) return;
    this.#gridConfig = config;
    this.#sourcesChanged = true;
    // Clear light DOM cache for framework async content
    this.#lightDomColumnsCache = undefined;
  }

  /** Get the raw gridConfig source */
  getGridConfig(): GridConfig<T> | undefined {
    return this.#gridConfig;
  }

  /** Set columns source */
  setColumns(columns: ColumnConfig<T>[] | ColumnConfigMap<T> | undefined): void {
    this.#columns = columns;
    this.#sourcesChanged = true;
  }

  /** Get the raw columns source */
  getColumns(): ColumnConfig<T>[] | ColumnConfigMap<T> | undefined {
    return this.#columns;
  }

  /** Set fitMode source */
  setFitMode(mode: FitMode | undefined): void {
    this.#fitMode = mode;
    this.#sourcesChanged = true;
  }

  /** Get the raw fitMode source */
  getFitMode(): FitMode | undefined {
    return this.#fitMode;
  }

  /** Set columnInference source */
  setColumnInference(mode: ColumnInferenceMode | undefined): void {
    if (this.#columnInference === mode) return;
    this.#columnInference = mode;
    this.#sourcesChanged = true;
  }

  /** Get the raw columnInference source */
  getColumnInference(): ColumnInferenceMode | undefined {
    return this.#columnInference;
  }
  // #endregion

  // #region Config Lifecycle
  /**
   * Merge all sources into effective config.
   * Also applies post-merge operations (rowHeight, fixed mode widths, animation).
   *
   * Called by RenderScheduler's mergeConfig phase.
   *
   * Two-layer architecture:
   * 1. Sources → #originalConfig (frozen, immutable)
   * 2. Clone → #effectiveConfig (mutable, runtime changes)
   *
   * When sources change, both layers are rebuilt.
   * When sources haven't changed AND columns exist, this is a no-op.
   * Runtime mutations only affect effectiveConfig.
   * resetState() clones originalConfig back to effectiveConfig.
   */
  merge(): void {
    // Only rebuild when sources have actually changed.
    // Exception: always rebuild if we don't have columns yet (inference may be needed)
    const hasColumns = (this.#effectiveConfig.columns?.length ?? 0) > 0;
    if (!this.#sourcesChanged && hasColumns) {
      return; // effectiveConfig is already valid
    }

    // Build config from all sources
    const base = this.#collectAllSources();

    // Mark sources as processed
    this.#sourcesChanged = false;

    // Freeze as the new original config (immutable reference point)
    this.#originalConfig = base;
    Object.freeze(this.#originalConfig);
    if (this.#originalConfig.columns) {
      // Deep freeze columns array (but not the column objects themselves,
      // as we need effectiveConfig columns to be mutable)
      Object.freeze(this.#originalConfig.columns);
    }

    // Clone to effective config (mutable copy for runtime changes)
    this.#effectiveConfig = this.#cloneConfig(this.#originalConfig);

    // Apply post-merge operations to effectiveConfig
    this.#applyPostMergeOperations();
  }

  /**
   * Deep clone a config object, handling functions (renderers, editors).
   * Uses structuredClone where possible, with fallback for function properties.
   */
  #cloneConfig(config: GridConfig<T>): GridConfig<T> {
    // Can't use structuredClone because config may contain functions
    const clone: GridConfig<T> = { ...config };

    // Deep clone columns (they may have runtime-mutable state)
    if (config.columns) {
      clone.columns = config.columns.map((col) => ({ ...col }));
    }

    // Deep clone shell if present
    if (config.shell) {
      clone.shell = {
        ...config.shell,
        header: config.shell.header ? { ...config.shell.header } : undefined,
        toolPanel: config.shell.toolPanel ? { ...config.shell.toolPanel } : undefined,
        toolPanels: config.shell.toolPanels?.map((p) => ({ ...p })),
        headerContents: config.shell.headerContents?.map((h) => ({ ...h })),
      };
    }

    return clone;
  }

  /**
   * Apply operations that depend on the merged effective config.
   * These were previously in grid.ts #mergeEffectiveConfig().
   */
  #applyPostMergeOperations(): void {
    const config = this.#effectiveConfig;

    // Apply rowHeight from config if specified (only for numeric values)
    // Function-based rowHeight is handled by variable height virtualization
    if (typeof config.rowHeight === 'number' && config.rowHeight > 0) {
      this.#grid._virtualization.rowHeight = config.rowHeight;
      // Also drive the CSS variable: without it rows keep the theme height while
      // virtualization positions them at the configured one, leaving gaps.
      this.#grid.style.setProperty(ROW_HEIGHT_VAR, `${config.rowHeight}px`);
      this.#ownsRowHeightVar = true;
    } else if (this.#ownsRowHeightVar) {
      // Only ours is cleared — `--tbw-row-height` is a public theme variable and
      // consumers set it inline on the host (e.g. the calendar demos).
      this.#grid.style.removeProperty(ROW_HEIGHT_VAR);
      this.#ownsRowHeightVar = false;
    }

    // If fixed mode and width not specified: use minWidth when available,
    // otherwise fall back to default 80px.
    if (config.fitMode === 'fixed') {
      const columns = this.columns;
      columns.forEach((c) => {
        if (c.width == null) {
          c.width = typeof c.minWidth === 'number' && Number.isFinite(c.minWidth) && c.minWidth > 0 ? c.minWidth : 80;
        }
      });
    }

    // Apply animation configuration to host element
    this.#grid._applyAnimationConfig(config);
  }

  /**
   * Collect all sources into a single config object.
   * This is the core merge logic extracted from grid.ts #mergeEffectiveConfig.
   *
   * Collects all sources into a canonical config object.
   * This becomes the frozen #originalConfig.
   *
   * Sources (in order of precedence, low to high):
   * 1. gridConfig.columns
   * 2. Light DOM columns (merged with config columns)
   * 3. columns prop (overrides if set)
   * 4. Inferred columns (if still empty)
   *
   * Runtime state (hidden, width) is NOT preserved here - that's in effectiveConfig.
   */
  #collectAllSources(): GridConfig<T> {
    const base: GridConfig<T> = this.#gridConfig ? { ...this.#gridConfig } : {};
    const configColumns: ColumnConfig<T>[] = Array.isArray(base.columns) ? [...base.columns] : [];

    // Light DOM cached parse - clone to avoid mutation
    const domCols: ColumnConfig<T>[] = (this.#lightDomColumnsCache ?? []).map((c) => ({
      ...c,
    })) as ColumnConfig<T>[];

    // Use mergeColumns to combine config columns with light DOM columns
    // This handles all the complex merge logic including templates and renderers
    let columns: ColumnInternal<T>[] = mergeColumns(
      configColumns as ColumnInternal<T>[],
      domCols as ColumnInternal<T>[],
    ) as ColumnInternal<T>[];

    // Columns prop highest structural precedence (overrides merged result)
    if (this.#columns && (this.#columns as ColumnConfig<T>[]).length) {
      // When columns prop is set, merge with light DOM columns for renderers/templates
      columns = mergeColumns(
        this.#columns as ColumnInternal<T>[],
        domCols as ColumnInternal<T>[],
      ) as ColumnInternal<T>[];
    }

    // Inference. Mode resolved from standalone source (prop/attribute) then gridConfig.
    const rows = this.#grid.sourceRows;
    const inferenceMode: ColumnInferenceMode = this.#columnInference ?? base.columnInference ?? 'auto';
    if (inferenceMode === 'merge' && rows.length) {
      // Always infer the full column set from data, then overlay provided columns by field.
      const inferred = inferColumns(rows as Record<string, unknown>[]).columns as ColumnInternal<T>[];
      columns = overlayInferred(inferred, columns);
    } else if (columns.length === 0 && rows.length) {
      // 'auto' (default): infer only when nothing was provided.
      const result = inferColumns(rows as Record<string, unknown>[]);
      columns = result.columns as ColumnInternal<T>[];
    }

    // Declarative light-DOM <tbw-grid-type> defaults are merged with
    // programmatic `gridConfig.typeDefaults`.
    //
    // Precedence:
    // - Types only in light DOM: included.
    // - Types only in gridConfig: included.
    // - Same type in both: shallow-merged, gridConfig keys win.
    if (this.#lightDomTypeDefaultsCache && Object.keys(this.#lightDomTypeDefaultsCache).length > 0) {
      const merged: Record<string, TypeDefault<T>> = { ...this.#lightDomTypeDefaultsCache };
      const configured = base.typeDefaults ?? {};
      for (const typeName of Object.keys(configured)) {
        const fromConfig = configured[typeName];
        const fromLightDom = merged[typeName];
        if (fromLightDom) {
          merged[typeName] = {
            ...fromLightDom,
            ...fromConfig,
          };
        } else {
          merged[typeName] = fromConfig;
        }
      }
      base.typeDefaults = merged;
    }

    if (columns.length) {
      // Apply initial column ordering (before defaults and compilation)
      applyInitialOrder(columns);

      // Type defaults fill gaps before the per-column defaults below, so a type
      // default can still override `sortable` / `resizable`.
      if (base.typeDefaults) {
        applyTypeDefaults(columns, base.typeDefaults);
      }

      // Apply per-column defaults
      columns.forEach((c) => {
        if (c.sortable === undefined) c.sortable = true;
        if (c.resizable === undefined) c.resizable = true;
        if (c.__originalWidth === undefined && typeof c.width === 'number') {
          c.__originalWidth = c.width;
        }
      });

      // Compile inline templates (from light DOM <template> elements)
      columns.forEach((c) => {
        if (c.__viewTemplate && !c.__compiledView) {
          c.__compiledView = compileTemplate((c.__viewTemplate as HTMLElement).innerHTML);
        }
        if (c.__editorTemplate && !c.__compiledEditor) {
          c.__compiledEditor = compileTemplate(c.__editorTemplate.innerHTML);
        }
      });

      base.columns = columns as ColumnConfig<T>[];
    }

    // Individual prop overrides
    if (this.#fitMode) base.fitMode = this.#fitMode;
    if (!base.fitMode) base.fitMode = 'stretch';
    if (this.#columnInference) base.columnInference = this.#columnInference;

    // ========================================================================
    // Let plugins contribute to the effective config (e.g. the ShellPlugin
    // folds its light-DOM / API-registered shell state into base.shell). Core
    // holds no shell-config knowledge — this is the neutral processConfig hook.
    // ========================================================================
    this.#grid._pluginManager?.processConfig?.(base);

    // Store columnState from gridConfig if not already set
    if (base.columnState && !this.#initialColumnState) {
      this.#initialColumnState = base.columnState;
    }

    return base;
  }
  // #endregion

  // #region State Persistence
  /**
   * Collect current column state by diffing original vs effective.
   * Returns only the changes from the original configuration.
   */
  collectState(plugins: BaseGridPlugin[]): GridColumnState {
    const columns = this.columns;
    const sortStates = this.#getSortState();

    return {
      columns: columns.map((col, index) => {
        const state: ColumnState = {
          field: col.field,
          order: index,
          visible: !col.hidden,
        };

        // Include width if set
        const internalCol = col as ColumnInternal<T>;
        if (internalCol.__renderedWidth !== undefined) {
          state.width = internalCol.__renderedWidth;
        } else if (col.width !== undefined) {
          state.width = typeof col.width === 'string' ? parseFloat(col.width) : col.width;
        }

        // Include sort state
        const sortState = sortStates.get(col.field);
        if (sortState) {
          state.sort = sortState;
        }

        // Collect from plugins
        for (const plugin of plugins) {
          if (plugin.getColumnState) {
            const pluginState = plugin.getColumnState(col.field);
            if (pluginState) {
              Object.assign(state, pluginState);
            }
          }
        }

        return state;
      }),
    };
  }

  /**
   * Apply column state to the grid.
   */
  applyState(state: GridColumnState, plugins: BaseGridPlugin[]): void {
    if (!state.columns || state.columns.length === 0) return;

    const allColumns = this.columns;
    const stateMap = new Map(state.columns.map((s) => [s.field, s]));

    // Apply width and visibility
    const updatedColumns = allColumns.map((col) => {
      const s = stateMap.get(col.field);
      if (!s) return col;

      const updated: ColumnInternal<T> = { ...col };

      if (s.width !== undefined) {
        updated.width = s.width;
        updated.__renderedWidth = s.width;
      }

      if (s.visible !== undefined) {
        updated.hidden = !s.visible;
      }

      return updated;
    });

    // Reorder columns
    updatedColumns.sort((a, b) => {
      const orderA = stateMap.get(a.field)?.order ?? Infinity;
      const orderB = stateMap.get(b.field)?.order ?? Infinity;
      return orderA - orderB;
    });

    this.columns = updatedColumns;

    // Apply sort state
    const sortedByPriority = state.columns
      .filter((s) => s.sort !== undefined)
      .sort((a, b) => (a.sort?.priority ?? 0) - (b.sort?.priority ?? 0));

    if (sortedByPriority.length > 0) {
      const primarySort = sortedByPriority[0];
      if (primarySort.sort) {
        this.#grid._sortState = {
          field: primarySort.field,
          direction: primarySort.sort.direction === 'asc' ? 1 : -1,
        };
      }
    } else {
      this.#grid._sortState = null;
    }

    // Let plugins apply their state
    for (const plugin of plugins) {
      if (plugin.applyColumnState) {
        for (const colState of state.columns) {
          plugin.applyColumnState(colState.field, colState);
        }
      }
    }
  }

  /**
   * Reset state to original configuration.
   *
   * Two-layer architecture: Clones #originalConfig back to #effectiveConfig.
   * This discards all runtime changes (hidden, width, order) and restores
   * the state to what was compiled from sources.
   */
  resetState(plugins: BaseGridPlugin[]): void {
    // Clear initial state
    this.#initialColumnState = undefined;

    // Reset sort state
    this.#grid._sortState = null;

    // Clone original config back to effective (discards all runtime changes)
    this.#effectiveConfig = this.#cloneConfig(this.#originalConfig);

    // Apply post-merge operations (rowHeight, fixed mode widths, animation)
    this.#applyPostMergeOperations();

    // Notify plugins to reset
    for (const plugin of plugins) {
      if (plugin.applyColumnState) {
        for (const col of this.columns) {
          plugin.applyColumnState(col.field, {
            field: col.field,
            order: 0,
            visible: true,
          });
        }
      }
    }

    // Request state change notification
    this.requestStateChange(plugins);
  }

  /**
   * Get sort state as a map.
   */
  #getSortState(): Map<string, ColumnSortState> {
    const sortMap = new Map<string, ColumnSortState>();
    const sortState = this.#grid._sortState;

    if (sortState) {
      sortMap.set(sortState.field, {
        direction: sortState.direction === 1 ? 'asc' : 'desc',
        priority: 0,
      });
    }

    return sortMap;
  }

  /**
   * Request a debounced state change event.
   */
  requestStateChange(plugins: BaseGridPlugin[]): void {
    if (this.#stateChangeTimeoutId) {
      clearTimeout(this.#stateChangeTimeoutId);
    }

    this.#stateChangeTimeoutId = setTimeout(() => {
      this.#stateChangeTimeoutId = undefined;
      const state = this.collectState(plugins);
      this.#grid._emit('column-state-change', state);
    }, STATE_CHANGE_DEBOUNCE_MS);
  }
  // #endregion

  // #region Column Visibility API
  /**
   * Set the visibility of a column.
   * @returns true if visibility changed, false otherwise
   */
  setColumnVisible(field: string, visible: boolean): boolean {
    const allCols = this.columns;
    const col = allCols.find((c) => c.field === field);

    if (!col) return false;
    if (!visible && col.lockVisible) return false;

    // Ensure at least one column remains visible
    if (!visible) {
      const remainingVisible = allCols.filter((c) => !c.hidden && c.field !== field).length;
      if (remainingVisible === 0) return false;
    }

    const wasHidden = !!col.hidden;
    if (wasHidden === !visible) return false; // No change

    col.hidden = !visible;

    this.#grid._emit('column-visibility', {
      field,
      visible,
      visibleColumns: allCols.filter((c) => !c.hidden).map((c) => c.field),
    });

    this.#grid._clearRowPool();
    this.#grid._setup();

    return true;
  }

  /**
   * Toggle column visibility.
   */
  toggleColumnVisibility(field: string): boolean {
    const col = this.columns.find((c) => c.field === field);
    return col ? this.setColumnVisible(field, !!col.hidden) : false;
  }

  /**
   * Check if a column is visible.
   */
  isColumnVisible(field: string): boolean {
    const col = this.columns.find((c) => c.field === field);
    return col ? !col.hidden : false;
  }

  /**
   * Show all columns.
   */
  showAllColumns(): void {
    const allCols = this.columns;
    if (!allCols.some((c) => c.hidden)) return;

    allCols.forEach((c) => (c.hidden = false));

    this.#grid._emit('column-visibility', {
      visibleColumns: allCols.map((c) => c.field),
    });

    this.#grid._clearRowPool();
    this.#grid._setup();
  }

  /**
   * Get all columns with visibility info.
   */
  getAllColumns(): Array<{
    field: string;
    header: string;
    visible: boolean;
    lockVisible?: boolean;
    utility?: boolean;
  }> {
    return this.columns.map((c) => ({
      field: c.field,
      header: c.header || c.field,
      visible: !c.hidden,
      lockVisible: c.lockVisible,
      utility: c.utility === true,
    }));
  }

  /**
   * Get current column order.
   */
  getColumnOrder(): string[] {
    return this.columns.map((c) => c.field);
  }

  /**
   * Set column order.
   */
  setColumnOrder(order: string[]): void {
    if (!order.length) return;

    const columnMap = new Map(this.columns.map((c) => [c.field as string, c]));
    const reordered: ColumnInternal<T>[] = [];

    for (const field of order) {
      const col = columnMap.get(field);
      if (col) {
        reordered.push(col);
        columnMap.delete(field);
      }
    }

    // Add remaining columns not in order
    for (const col of columnMap.values()) {
      reordered.push(col);
    }

    this.columns = reordered;

    // Invalidate the grid's visible-columns cache. The columns setter above
    // updates effectiveConfig.columns (shared with the grid), but the grid
    // caches _visibleColumns and only invalidates it via its own _columns setter.
    // Without this, renderHeader and row rendering read the stale cached order.
    this.#grid._invalidateVisibleColumnsCache();

    renderHeader(this.#grid);
    updateTemplate(this.#grid);
    // Column reorder is a structural change: request COLUMNS phase so processColumns()
    // bumps __rowRenderEpoch, forcing full cell rebuilds (renderInlineRow) instead of
    // content-only patches (fastPatchRow). Without this, body cells retain stale
    // data-field/data-col attributes and the FLIP animation applies wrong deltas.
    this.#grid._requestSchedulerPhase(RenderPhase.COLUMNS, 'configManager');
  }
  // #endregion

  // #region Light DOM Observer
  /**
   * Parse light DOM columns from host element.
   */
  parseLightDomColumns(host: HTMLElement): void {
    if (!this.#lightDomColumnsCache) {
      this.#originalColumnNodes = Array.from(host.querySelectorAll('tbw-grid-column')) as HTMLElement[];
      this.#lightDomColumnsCache = this.#originalColumnNodes.length ? parseLightDomColumns(host) : [];
    }

    if (!this.#lightDomTypeDefaultsCache) {
      const defs = parseLightDomTypeDefaults(host);
      const typeDefaults: Record<string, TypeDefault<T>> = {};

      for (const def of defs) {
        const typeDefault: TypeDefault<T> = {};
        let hasAnyDefault = false;

        if (def.viewRenderer) {
          typeDefault.renderer = def.viewRenderer as TypeDefault<T>['renderer'];
          hasAnyDefault = true;
        } else if (def.viewTemplate) {
          const viewTemplate = compileTemplate(def.viewTemplate.innerHTML);
          typeDefault.renderer = (ctx) =>
            viewTemplate({
              row: ctx.row as T,
              value: ctx.value,
              field: ctx.field,
              column: ctx.column as ColumnInternal<T>,
              typeDefault: def.params,
            });
          hasAnyDefault = true;
        }

        if (!hasAnyDefault) continue;

        typeDefaults[def.name] = typeDefault;
      }

      this.#lightDomTypeDefaultsCache = typeDefaults;
    }
  }

  /**
   * Clear the light DOM columns cache.
   */
  clearLightDomCache(): void {
    this.#lightDomColumnsCache = undefined;
    this.#lightDomTypeDefaultsCache = undefined;
  }

  /**
   * Registered Light DOM element handlers.
   * Maps element tag names to callbacks that are invoked when those elements change.
   *
   * This is a generic mechanism - plugins (or future ShellPlugin) register
   * what elements they care about and handle parsing themselves.
   */
  #lightDomHandlers: Map<string, () => void> = new Map();

  /**
   * Register a handler for Light DOM element changes.
   * When elements matching the tag name are added/removed/changed,
   * the callback will be invoked (debounced).
   *
   * @param tagName - The lowercase tag name to watch (e.g., 'tbw-grid-header')
   * @param callback - Called when matching elements change
   */
  registerLightDomHandler(tagName: string, callback: () => void): void {
    this.#lightDomHandlers.set(tagName.toLowerCase(), callback);
  }

  /**
   * Unregister a Light DOM element handler.
   */
  unregisterLightDomHandler(tagName: string): void {
    this.#lightDomHandlers.delete(tagName.toLowerCase());
  }

  /**
   * Set up MutationObserver to watch for Light DOM changes.
   * This is generic infrastructure - specific handling is done via registered handlers.
   *
   * When Light DOM elements are added/removed/changed, the observer:
   * 1. Identifies which registered tag names were affected
   * 2. Debounces multiple mutations into a single callback per handler
   * 3. Invokes the registered callbacks
   *
   * This mechanism allows plugins to register their own Light DOM elements
   * and handle parsing themselves, then hand config to ConfigManager.
   *
   * @param host - The host element to observe (the grid element)
   */
  observeLightDOM(host: HTMLElement): void {
    // Clean up any existing observer
    if (this.#lightDomObserver) {
      this.#lightDomObserver.disconnect();
    }

    // Track which handlers need to be called (debounced)
    const pendingCallbacks = new Set<string>();

    const processPendingCallbacks = () => {
      this.#lightDomDebounceTimer = undefined;
      for (const tagName of pendingCallbacks) {
        const handler = this.#lightDomHandlers.get(tagName);
        handler?.();
      }
      pendingCallbacks.clear();
    };

    this.#lightDomObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        // Check added nodes
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const el = node as Element;
          const tagName = el.tagName.toLowerCase();

          // Check if any handler is interested in this element
          if (this.#lightDomHandlers.has(tagName)) {
            pendingCallbacks.add(tagName);
          }
        }

        // Check removed nodes
        for (const node of mutation.removedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const el = node as Element;
          const tagName = el.tagName.toLowerCase();

          if (this.#lightDomHandlers.has(tagName)) {
            pendingCallbacks.add(tagName);
          }
        }

        // Check for attribute changes
        if (mutation.type === 'attributes' && mutation.target.nodeType === Node.ELEMENT_NODE) {
          const el = mutation.target as Element;
          const tagName = el.tagName.toLowerCase();
          if (this.#lightDomHandlers.has(tagName)) {
            pendingCallbacks.add(tagName);
          }
        }
      }

      // Debounce - batch all mutations into single callbacks
      if (pendingCallbacks.size > 0 && !this.#lightDomDebounceTimer) {
        this.#lightDomDebounceTimer = setTimeout(processPendingCallbacks, 0);
      }
    });

    // Observe children and their attributes.
    //
    // The grid renders into light DOM (no Shadow DOM), so this subtree also
    // contains every rendered row/cell. `attributeFilter` is a deliberate
    // performance guard: without it, every class/aria-selected/tabindex/style
    // mutation on every cell during scroll/selection/focus would generate a
    // MutationRecord. The list is limited to the config-carrying attributes on
    // light-DOM config elements (`<tbw-grid-column>`, `<tbw-grid-type>`, shell
    // elements). Note: arbitrary `data-*` mutations are intentionally NOT
    // reactive (mirrors columns) — swap the element (a childList mutation) to
    // re-parse a declarative type/column definition.
    this.#lightDomObserver.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'title',
        'field',
        'header',
        'width',
        'hidden',
        'id',
        'icon',
        'tooltip',
        'order',
        'name',
        'type',
      ],
    });
  }
  // #endregion

  // #region Cleanup
  /**
   * Dispose of the ConfigManager and clean up resources.
   */
  dispose(): void {
    this.#lightDomObserver?.disconnect();
    if (this.#stateChangeTimeoutId) {
      clearTimeout(this.#stateChangeTimeoutId);
    }
    if (this.#lightDomDebounceTimer) {
      clearTimeout(this.#lightDomDebounceTimer);
      this.#lightDomDebounceTimer = undefined;
    }
  }
  // #endregion
}
